import React, { useState, useEffect, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
const Hero3D = lazy(() => import("./Hero3D"));
import { AhJinLogo } from "./AhJinLogo";
import { askLink } from "../lib/whatsapp";
import { isBot } from "../lib/geo";
import { openBooking } from "../lib/booking";
import WhatsAppIcon from "./WhatsAppIcon";
import { CalendarCheck } from "lucide-react";

const ROTATING_WORDS = ["Brokerages", "Prop Firms", "Trading Platforms", "Regulated Ventures", "Founders"];

const RotatingAccent = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setIdx((i) => (i + 1) % ROTATING_WORDS.length), 2600);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="mt-6 flex items-center gap-3 text-sm sm:text-base justify-center lg:justify-start" data-testid="hero-rotating-accent">
      <span className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">Built for</span>
      <span className="relative inline-block h-7 overflow-hidden min-w-[220px] text-left">
        <AnimatePresence initial={false}>
          <motion.span
            key={ROTATING_WORDS[idx]}
            initial={{ y: 22, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -22, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 top-0 font-display italic text-xl sm:text-2xl gold-text whitespace-nowrap"
          >
            {ROTATING_WORDS[idx]}
          </motion.span>
        </AnimatePresence>
      </span>
    </div>
  );
};

/* Skip the WebGL scene on low-power devices / data-saver so the page stays fast */
const lowPower = () => {
  if (window.location.search.includes("fx=3d")) return false;
  if (isBot()) return true;
  const n = navigator;
  return (n.hardwareConcurrency && n.hardwareConcurrency <= 4) || (n.deviceMemory && n.deviceMemory <= 4) || n.connection?.saveData === true;
};

const PROOF = ["9+ launches", "15+ jurisdictions", "UAE · Cyprus · LATAM", "Since 2015"];

const Hero = () => {
  const [show3D, setShow3D] = useState(false);

  // let the headline paint first, then start the 3D scene
  useEffect(() => {
    if (lowPower()) return;
    const start = () => setShow3D(true);
    const idle = window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 1200 }) : setTimeout(start, 700);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(idle) : clearTimeout(idle));
  }, []);

  return (
    <>
    <section
      id="home"
      className="relative min-h-[78vh] w-full flex items-start justify-center overflow-hidden grain pt-[9vh] sm:pt-[13vh] pb-14 sm:pb-12"
      data-testid="hero-section"
    >
      {/* layered backdrop */}
      <div className="absolute inset-0 bg-[#050b1c]/85" />
      <div className="aurora" />
      <div className="absolute inset-0 grid-lines opacity-60" />

      {/* purple bokeh */}
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(126,34,206,0.28) 0%, transparent 70%)" }} />
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(91,33,182,0.26) 0%, transparent 70%)" }} />

      {/* Liquid-metal 3D centerpiece */}
      {show3D && (
        <div className="absolute inset-0 hidden md:block">
          <Suspense fallback={null}>
            <Hero3D className="absolute inset-x-0 top-0 h-full pointer-events-none opacity-80" />
          </Suspense>
        </div>
      )}

      {/* Phoenix emblem — flat backdrop on mobile and on low-power devices where 3D is skipped */}
      <motion.img
        src="/phoenix-emblem-3d.webp"
        alt=""
        aria-hidden="true"
        data-testid="hero-phoenix-emblem-mobile"
        className={`absolute pointer-events-none select-none left-1/2 -translate-x-1/2 top-[5%] w-[260px] ${show3D ? "md:hidden" : "md:left-[68%] md:top-[14%] md:w-[420px]"}`}
        style={{ filter: "drop-shadow(0 0 40px rgba(139,92,246,0.55)) drop-shadow(0 8px 18px rgba(0,0,0,0.5))" }}
        initial={{ opacity: 0, y: 40, scale: 0.9 }}
        animate={{ opacity: 0.55, y: [0, -10, 0], scale: 1 }}
        transition={{ opacity: { duration: 1.4, ease: "easeOut" }, scale: { duration: 1.4, ease: "easeOut" }, y: { duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1.4 } }}
      />

      {/* corner labels — dedicated strip just under the header, above everything else in hero */}
      <div className="absolute top-[84px] inset-x-0 z-40 px-6 sm:px-10 pointer-events-none hidden sm:flex items-center justify-between text-[10px] tracking-[0.5em] uppercase text-zinc-400">
        <span className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rotate-45" style={{ background: "linear-gradient(135deg,#E5C56F,#9C7E2F)", boxShadow: "0 0 8px rgba(229,197,111,0.6)" }} />
          EST · 2015 · UAE
        </span>
        <span className="flex items-center gap-2">
          LATAM · Europe · Global
          <span className="w-1.5 h-1.5 rotate-45" style={{ background: "linear-gradient(135deg,#E5C56F,#9C7E2F)", boxShadow: "0 0 8px rgba(229,197,111,0.6)" }} />
        </span>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
        {/* LEFT — logo + headline */}
        <div className="lg:col-span-7 flex flex-col items-center text-center lg:items-start lg:text-left lg:mt-16 lg:-ml-8">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="float-slow"
          >
            <AhJinLogo size={105} animate showWordmark showMark={false} align="left" />
          </motion.div>

          {/* Heritage ornament eyebrow */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 lg:mt-7"
          >
            <span className="heritage-eyebrow">
              <span className="flourish not-italic">Since 2015</span> · Brokerage & Prop-Firm Consultancy
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.85, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="font-display mt-4 text-[1.75rem] sm:text-4xl lg:text-[2.7rem] text-zinc-100 leading-[1.12] max-w-xl tracking-tight"
            data-testid="hero-headline"
          >
            Launch your brokerage or prop firm{" "}
            <em className="not-italic gold-text">faster.</em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 sm:mt-4 text-[13px] sm:text-[15px] text-zinc-300 leading-relaxed max-w-lg"
            data-testid="hero-subline"
          >
            We take brokerage and prop-firm founders from idea to live business —
            incorporation, licensing, platforms, liquidity and payments. One
            accountable team since 2015. The first step is a 30-minute strategy
            call.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 flex flex-wrap items-center gap-3 justify-center lg:justify-start"
          >
            <button
              type="button"
              onClick={() => openBooking("hero")}
              className="inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#1a1405] hover:brightness-110 hover:-translate-y-0.5 transition-all shadow-[0_0_28px_rgba(229,197,111,0.25)]"
              data-testid="hero-whatsapp-btn"
            >
              <CalendarCheck size={16} />
              Book a $250 Strategy Call
            </button>
            <a
              href={askLink("launching a brokerage or prop firm")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-purple-500/40 bg-purple-600/10 px-6 py-3 text-[12px] uppercase tracking-[0.2em] text-purple-200 hover:border-purple-400/60 hover:bg-purple-600/20 hover:-translate-y-0.5 transition-all"
              data-testid="hero-ask-btn"
            >
              <WhatsAppIcon size={15} />
              Ask a question
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 text-[11px] tracking-[0.15em] uppercase text-zinc-500 text-center lg:text-left"
            data-testid="hero-consult-note"
          >
            30 minutes · written launch map · <span className="gold-text">$250 credited toward your engagement</span>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 justify-center lg:justify-start"
            data-testid="hero-proof-row"
          >
            {PROOF.map((p) => (
              <li key={p} className="flex items-center gap-2 text-[11px] text-zinc-400">
                <span className="w-1 h-1 rotate-45 bg-[#E5C56F] shadow-[0_0_6px_rgba(229,197,111,0.7)]" />
                {p}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <RotatingAccent />
          </motion.div>
        </div>

      </div>
    </section>

    </>
  );
};

export default Hero;
